<template>
    <aside class="sidebar-options" :class="{ collapsed: !localOpen }">
        <template v-if="localOpen">
            <div class="sidebar-header ppv2-p-6 !ppv2-pb-2">
                <h3 class="poppinsBold ppv2-text-base ppv2-text-black">{{ $t('devis.v3.options_title') }}</h3>
                <button @click="toggle">
                    <PanelRightClose />
                </button>
            </div>

            <div class="sidebar-content ppv2-p-6 !ppv2-pt-2">
                <div class="option-group">
                    <h4 class="group-title poppinsBold ppv2-text-sm ppv2-text-black">{{ $t('devis.v3.conditions_title') }}</h4>
                    <p class="group-desc poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-70">
                        {{ $t('devis.v3.conditions_desc') }}
                    </p>

                    <div class="field">
                        <BaseSelect
                            v-model="form.validity"
                            :options="validityOptions"
                            :clearable="false"
                            :searchable="false"
                            classAdded="v3-quote-select"
                            :label="$t('devis.v3.validity_label')"
                            custom-label-class="poppinsMedium ppv2-text-sm ppv2-text-black"
                            customInputClass="!ppv2-opacity-100"
                        />
                    </div>

                    <div class="field">
                        <BaseSelect
                            v-model="form.auto_reminder"
                            :options="reminderOptions"
                            :clearable="false"
                            :searchable="false"
                            classAdded="v3-quote-select"
                            :label="$t('devis.v3.auto_reminder_label')"
                            custom-label-class="poppinsMedium ppv2-text-sm ppv2-text-black"
                            customInputClass="!ppv2-opacity-100"
                        />
                    </div>

                    <!-- Masqué temporairement (fonctionnalité non livrée) : voir showBlockResources. -->
                    <div v-if="showBlockResources" class="field">
                        <BaseSelect
                            v-model="form.block_resources"
                            :options="blockResourcesOptions"
                            :clearable="false"
                            :searchable="false"
                            classAdded="v3-quote-select"
                            :label="$t('devis.v3.block_resources_label')"
                            custom-label-class="poppinsMedium ppv2-text-sm ppv2-text-black"
                            customInputClass="!ppv2-opacity-100"
                        />
                    </div>
                </div>

                <div class="option-group">
                    <h4 class="group-title poppinsMedium ppv2-text-sm ppv2-text-black">
                        {{ $t('devis.v3.payment_methods_title') }}
                    </h4>

                    <!-- Moyens de paiement dynamiques : source Establishment::payments
                         (même référentiel que la caisse nomade V3). -->
                    <div v-if="paymentMethods.length" class="ppv2-flex ppv2-flex-col ppv2-gap-4">
                        <template v-for="method in paymentMethods">
                            <!-- Virement : carte étendue avec RIB de l'établissement -->
                            <DashedCard
                                v-if="isVirement(method)"
                                :key="method.id"
                                :marginTop="10"
                                :marginBottom="0"
                                custom-class-title="!ppv2-px-0"
                            >
                                <template #title>
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2 !ppv2-bg-[#d2ddf429] ppv2-px-1">
                                        <BaseSwitch v-model="form.payments[method.id]" size="md" />
                                        <span class="poppinsMedium ppv2-text-sm ppv2-text-black">{{ methodLabel(method) }}</span>
                                    </div>
                                </template>
                                <template #content>
                                    <div v-if="form.payments[method.id]" class="ppv2-p-4 ppv2-flex ppv2-flex-col ppv2-gap-1">
                                        <!-- Masqué temporairement (e-virement Stripe non livré) : voir showEVirement.
                                             form.e_virement reste à false → le RIB de l'établissement est toujours affiché. -->
                                        <BaseCheckbox
                                            v-if="showEVirement"
                                            v-model="form.e_virement"
                                            :label="$t('devis.v3.activate_evirement')"
                                            classAdd="ppv2-my-2"
                                            hasBorderChecked
                                        />

                                        <!-- RIB : repris automatiquement des Paramètres de l'établissement.
                                             Un champ déjà renseigné est verrouillé (pas de ressaisie) ;
                                             un champ vide est saisissable une première fois et sera
                                             enregistré dans les Paramètres. -->
                                        <template v-if="!form.e_virement">
                                            <CustomInput
                                                :label="$t('devis.v3.etablissement')"
                                                :value="bank.bank_name || ''"
                                                :placeholder="$t('devis.v3.etablissement')"
                                                readonly
                                                classInputAdd="bank-field-locked"
                                                customLabelClass="poppinsMedium ppv2-text-sm"
                                                classContainerAdd="!ppv2-mb-2 !ppv2-opacity-100"
                                            />
                                            <CustomInput
                                                :label="$t('devis.v3.iban')"
                                                :value="bankDraft.iban"
                                                :readonly="isBankFieldLocked('iban')"
                                                :classInputAdd="bankFieldClass('iban')"
                                                :errorText="bankErrors.iban"
                                                placeholder="FRXXX XXXX XXXX XXXX XXXX"
                                                customLabelClass="poppinsMedium ppv2-text-sm"
                                                classContainerAdd="!ppv2-mb-2 !ppv2-opacity-100"
                                                @input="onBankInput('iban', $event)"
                                            />
                                            <CustomInput
                                                :label="$t('devis.v3.bic')"
                                                :value="bankDraft.swift"
                                                :readonly="isBankFieldLocked('swift')"
                                                :classInputAdd="bankFieldClass('swift')"
                                                :errorText="bankErrors.swift"
                                                placeholder="XXXXXXXX"
                                                customLabelClass="poppinsMedium ppv2-text-sm"
                                                classContainerAdd="!ppv2-mb-0 !ppv2-opacity-100"
                                                @input="onBankInput('swift', $event)"
                                            />

                                            <p
                                                v-if="showBankFirstEntryHint"
                                                class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-70 ppv2-mt-2"
                                            >
                                                {{ $t('devis.v3.bank_first_entry_hint') }}
                                            </p>
                                        </template>

                                        <InfoBanner v-else variant="info" :text="$t('devis.v3.e_virement_banner_text')" />
                                    </div>
                                    <div v-else class="ppv2-h-8"></div>
                                </template>
                            </DashedCard>

                            <!-- Autres moyens : simple toggle -->
                            <DashedCard v-else :key="method.id" :marginTop="0" :marginBottom="0">
                                <template #content>
                                    <div class="ppv2-p-4 ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <BaseSwitch v-model="form.payments[method.id]" size="md" />
                                        <span class="poppinsMedium ppv2-text-sm ppv2-text-black">{{ methodLabel(method) }}</span>
                                    </div>
                                </template>
                            </DashedCard>
                        </template>
                    </div>

                    <p v-else class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-70">
                        {{ $t('devis.v3.no_payment_methods') }}
                    </p>
                </div>

                <div class="option-group !ppv2-border-none">
                    <h4 class="group-title poppinsBold ppv2-text-sm ppv2-text-black ppv2-mb-4">
                        {{ $t('devis.v3.esignature_title') }}
                    </h4>
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                        <BaseSwitch v-model="form.e_signature" size="md" />
                        <span class="poppinsMedium ppv2-text-sm ppv2-text-black">{{ $t('devis.v3.request_esignature') }}</span>
                    </div>
                </div>

                <div v-if="hasTemplates" class="option-group !ppv2-border-none !ppv2-pt-0">
                    <h4 class="group-title poppinsBold ppv2-text-sm ppv2-text-black ppv2-mb-4">
                        {{ $t('devis.v3.template_label') }}
                    </h4>
                    <div class="field">
                        <BaseSelect
                            v-model="form.template_id"
                            :options="templateOptions"
                            :clearable="false"
                            custom-label-class="poppinsMedium ppv2-text-sm ppv2-text-black"
                            customInputClass="!ppv2-opacity-100"
                            @input="onTemplateChange"
                        />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="collapsed-content ppv2-p-4">
                <button @click="toggle">
                    <PanelRightOpen />
                </button>
            </div>
        </template>
    </aside>
</template>

<script>
import { PanelRightClose, PanelRightOpen } from 'lucide-vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import DashedCard from '../../Shared/DashedCard.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import InfoBanner from '../../Shared/InfoBanner.vue'

export default {
    components: {
        PanelRightClose,
        PanelRightOpen,
        BaseSelect,
        DashedCard,
        BaseSwitch,
        CustomInput,
        BaseCheckbox,
        InfoBanner,
    },
    props: {
        form: {
            type: Object,
            required: true,
        },
        isOpen: {
            type: Boolean,
            default: true,
        },
        templates: {
            type: Array,
            default: () => [],
        },
        // Moyens de paiement de l'établissement (Establishment::payments) : [{ id, name, display_name }]
        paymentMethods: {
            type: Array,
            default: () => [],
        },
        // Coordonnées bancaires de l'établissement :
        // { bank_name, iban, swift, bank_address, iban_locked, swift_locked, can_edit }
        bank: {
            type: Object,
            default: () => ({}),
        },
        // Erreurs de sauvegarde du RIB, par champ : { iban: '...', swift: '...' }
        bankErrors: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        validityOptions() {
            const options = [
                { label: this.$t('devis.v3.validity_1month'), value: 30 },
                { label: this.$t('devis.v3.validity_15days'), value: 15 },
                { label: this.$t('devis.v3.validity_7days'), value: 7 },
            ]

            // Devis existant dont l'échéance ne tombe sur aucune durée standard (saisie au
            // calendrier avant que l'échéance ne devienne calculée) : on expose sa durée
            // réelle, sinon le sélecteur s'afficherait vide ou mentirait sur la date affichée.
            const current = this.form.validity
            if (current && !options.some((option) => option.value === current)) {
                options.unshift({ label: this.$t('devis.v3.validity_custom_days', { days: current }), value: current })
            }

            return options
        },
        // Valeurs en JOURS avant échéance, telles qu'enregistrées en base et consommées par
        // AutoRelaunchDevisJob. Les anciennes chaînes ('7_before'…) ne voulaient rien dire
        // pour le serveur : le sélecteur n'était alors branché sur rien.
        // Liste blanche miroir de DevisAutoRelaunchService::ALLOWED_DAYS.
        reminderOptions() {
            return [
                { label: this.$t('devis.v3.reminder_7before'), value: 7 },
                { label: this.$t('devis.v3.reminder_3before'), value: 3 },
                { label: this.$t('devis.v3.reminder_1before'), value: 1 },
            ]
        },
        blockResourcesOptions() {
            return [{ label: this.$t('devis.v3.block_resources_none'), value: 'none' }]
        },
        hasTemplates() {
            return this.templates.length > 0
        },
        templateOptions() {
            const opts = [{ label: this.$t('devis.v3.select_template'), value: -1 }]
            this.templates.forEach((t) => {
                opts.push({ label: t.title, value: t.id })
            })
            return opts
        },
        // Au moins un champ du RIB est encore saisissable : on explique que la saisie
        // alimentera les Paramètres de l'établissement.
        showBankFirstEntryHint() {
            return !this.isBankFieldLocked('iban') || !this.isBankFieldLocked('swift')
        },
    },
    data() {
        return {
            localOpen: typeof window !== 'undefined' ? window.innerWidth >= 1280 : true,
            // Options masquées tant que la fonctionnalité n'est pas livrée.
            // Repasser à true pour les réafficher (aucun autre changement nécessaire).
            showBlockResources: false,
            showEVirement: false,
            // Saisie locale du RIB (première saisie uniquement) — la source de vérité
            // reste l'établissement, renvoyée par le serveur après enregistrement.
            bankDraft: { iban: '', swift: '' },
        }
    },
    watch: {
        isOpen(val) {
            this.localOpen = val
        },
        bank: {
            immediate: true,
            deep: true,
            handler(value) {
                this.bankDraft = { iban: value?.iban || '', swift: value?.swift || '' }
            },
        },
    },
    mounted() {
        this._onResize = () => {
            this.localOpen = window.innerWidth >= 1280
        }
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._onResize)
    },
    methods: {
        // Libellé affiché : même logique que la caisse nomade (PaymentDialog LABELS),
        // overrides fixes puis repli sur le name du moyen de paiement.
        methodLabel(method) {
            const LABELS = {
                CB: 'Carte bancaire',
                'Pass culture': 'Pass Culture',
                'Points de fidélité': 'Fidélité',
            }
            return LABELS[method.name] || method.name
        },
        // Le virement bénéficie d'une carte étendue (RIB + option e-virement).
        isVirement(method) {
            return (method.name || '').toLowerCase() === 'virement'
        },
        // Verrouillé si la valeur existe déjà dans les Paramètres (reprise automatique,
        // pas de ressaisie) ou si l'utilisateur n'a pas le droit de modifier les paramètres.
        // Purement visuel : la règle est réappliquée côté serveur.
        isBankFieldLocked(field) {
            const lockedFlag = field === 'iban' ? this.bank?.iban_locked : this.bank?.swift_locked
            const alreadySet = lockedFlag !== undefined ? Boolean(lockedFlag) : Boolean(this.bank?.[field])
            return alreadySet || !this.bank?.can_edit
        },
        // La saisie remonte au parent à chaque frappe : c'est lui qui l'enregistre au
        // moment du "Enregistrer" du devis (un blur n'est pas déclenché de façon fiable
        // quand le champ a déjà perdu le focus).
        // Rendu grisé + curseur "interdit" quand le champ n'est pas saisissable :
        // l'attribut readonly seul laisse croire que le champ est modifiable.
        bankFieldClass(field) {
            return this.isBankFieldLocked(field) ? 'bank-field-locked' : ''
        },
        onBankInput(field, value) {
            if (this.isBankFieldLocked(field)) return
            this.$set(this.bankDraft, field, value)
            this.$emit('clear-bank-error', field)
            this.$emit('bank-draft', { field, value })
        },
        onTemplateChange() {
            if (this._templateChanging) return
            this._templateChanging = true
            this.$nextTick(() => {
                this._templateChanging = false
            })
            this.$emit('change-template')
        },
        toggle() {
            this.localOpen = !this.localOpen
            this.$emit('toggle')
        },
    },
}
</script>

<style scoped lang="scss">
/* Champ RIB non saisissable (valeur reprise des Paramètres) : il doit SE VOIR
   comme non modifiable. La valeur reste sélectionnable/copiable (readonly et non
   disabled), mais le fond grisé et le curseur "interdit" lèvent l'ambiguïté. */
::v-deep .input-container.bank-field-locked {
    background-color: rgba(15, 23, 42, 0.05);
    border-color: rgba(15, 23, 42, 0.08);
    cursor: not-allowed;

    input.custom-input {
        background-color: transparent;
        color: rgba(15, 23, 42, 0.55);
        cursor: not-allowed;
    }
}

::v-deep .dark .input-container.bank-field-locked {
    background-color: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);

    input.custom-input {
        color: rgba(255, 255, 255, 0.6);
    }
}

.sidebar-options {
    background: #d2ddf429;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    min-width: 300px;
    height: 75vh;

    &.collapsed {
        min-width: 60px !important;
        width: 60px;
        align-items: center;
        border: 1px solid #0000001a;
    }

    .sidebar-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
            margin: 0;
        }
    }

    .collapsed-content {
        display: flex;
        justify-content: center;
        width: 100%;
    }

    .sidebar-content {
        overflow-y: auto;
        max-height: calc(100vh - 120px);
    }
}

.option-group {
    padding-bottom: 24px;
    margin-bottom: 0;

    .group-title {
        margin-bottom: 4px;
    }

    .group-desc {
        line-height: 1.4;
        margin-bottom: 10px;
    }

    .field {
        margin-bottom: 10px;

        label {
            display: block;
            margin-bottom: 8px;
        }

        input {
            width: 100%;
            border: 1px solid #000000;
            border-radius: 12px;
            padding: 12px;
            font-size: 14px;
            background: white;
        }
    }
}
.\!ppv2-bg-\[\#d2ddf429\] {
    background-color: #d2ddf429 !important;
}
</style>
